import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import {
  Building2,
  UtensilsCrossed,
  Sparkles,
  Briefcase,
  Landmark,
  Rocket,
  ArrowUpRight,
} from "lucide-react";

const focus = [
  {
    icon: Building2,
    title: "Unternehmensbeteiligungen",
    text: "Mehrheits- und Minderheitsbeteiligungen an etablierten mittelständischen Unternehmen mit klarer operativer Substanz und nachvollziehbarem Geschäftsmodell.",
    testId: "focus-beteiligungen",
  },
  {
    icon: UtensilsCrossed,
    title: "Gastronomie",
    text: "Gastronomische Konzepte, Standorte und Betreiberstrukturen mit wirtschaftlicher Substanz, klarer Positionierung und professioneller Führung.",
    testId: "focus-gastronomie",
  },
  {
    icon: Landmark,
    title: "Immobilien",
    text: "Standortbewertung, Nutzungskonzepte und langfristige Entwicklungspotenziale – mit Fokus auf den Raum München und ausgewählte bayerische Lagen.",
    testId: "focus-immobilien",
  },
  {
    icon: Sparkles,
    title: "Events & Entertainment",
    text: "Veranstaltungs-, Entertainment- und Markenprojekte mit strategischem Anspruch, professioneller Umsetzung und definierter wirtschaftlicher Logik.",
    testId: "focus-events",
  },
  {
    icon: Briefcase,
    title: "Dienstleistungen",
    text: "Operative Dienstleistungs- und Verwaltungsgesellschaften, in denen klare Prozesse und Führung den nachhaltigen Wert ausmachen.",
    testId: "focus-dienstleistung",
  },
  {
    icon: Rocket,
    title: "Zukunftsorientierte Geschäftsmodelle",
    text: "Selektive Beteiligungen an Geschäftsmodellen mit nachvollziehbarem Marktbedarf, gesundem Kapitalbedarf und unternehmerisch geprägten Teams.",
    testId: "focus-zukunft",
  },
];

const criteria = [
  {
    label: "Region",
    value: "München · Bayern",
  },
  {
    label: "Beteiligungsform",
    value: "Mehrheits- & Minderheitsbeteiligung",
  },
  {
    label: "Horizont",
    value: "Langfristig, ohne festen Exit",
  },
  {
    label: "Voraussetzung",
    value: "Substanz, Führung, Verantwortung",
  },
];

export default function Investmentfokus() {
  useRevealOnScroll();

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="Investmentfokus | Wagenhofer Capital – Beteiligungen aus München"
        description="Strategische Beteiligungen in Unternehmen, Gastronomie, Immobilien, Events, Dienstleistungen und zukunftsorientierten Geschäftsmodellen. Investmentfokus von Wagenhofer Capital."
        path="/investmentfokus"
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "Investmentfokus", to: "/investmentfokus" }]} />

      <PageHeader
        testId="page-fields"
        overline="Investmentfokus"
        title={
          <>
            Sechs Felder.
            <br />
            <span className="text-zinc-400">Ein unternehmerischer Anspruch.</span>
          </>
        }
        subtitle="Wir investieren dort, wo wir aus eigener Erfahrung Substanz, Führung und Verantwortung zusammenführen können – mit langfristiger Perspektive und ohne festen Exit-Horizont."
      />

      <section
        data-testid="section-fields"
        className="section-y border-b border-white/5"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/5">
            {focus.map((field, idx) => {
              const Icon = field.icon;
              return (
                <article
                  key={field.title}
                  data-testid={field.testId}
                  className="group relative p-10 md:p-12 border-r border-b border-white/5 hover:bg-[#0a0a0a] transition-colors duration-500 reveal"
                  style={{ transitionDelay: `${idx * 60}ms` }}
                >
                  <span className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.25} />
                    <span className="overline text-zinc-600">
                      0{idx + 1}
                    </span>
                  </div>
                  <h2 className="font-display text-xl md:text-2xl tracking-tight font-medium mb-4">
                    {field.title}
                  </h2>
                  <p className="text-zinc-400 leading-relaxed font-light text-[15px]">
                    {field.text}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-y border-b border-white/5 bg-[#070707]">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-6">Investmentkriterien</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08]">
              Klare Kriterien.
              <br />
              <span className="text-zinc-400">Belastbare Entscheidungen.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:pl-8 reveal delay-1">
            <dl className="divide-y divide-white/10 border-t border-white/10">
              {criteria.map((c) => (
                <div
                  key={c.label}
                  className="grid grid-cols-3 gap-6 py-6"
                >
                  <dt className="overline text-zinc-500 col-span-1">
                    {c.label}
                  </dt>
                  <dd className="text-zinc-200 text-sm md:text-base col-span-2">
                    {c.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="reveal">
            <div className="overline mb-3">Nächster Schritt</div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
              Sie haben ein passendes Projekt oder Unternehmen?
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 reveal delay-1">
            <Link
              to="/ablauf"
              data-testid="fields-cta-process"
              className="group inline-flex items-center gap-3 border border-white/20 text-white hover:border-white hover:bg-white/5 transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
            >
              Ablauf ansehen
              <ArrowUpRight
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.5}
              />
            </Link>
            <Link
              to="/kontakt"
              data-testid="fields-cta-contact"
              className="group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
            >
              Kontakt aufnehmen
              <ArrowUpRight
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.5}
              />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
